// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader, X } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  fetchHorarios,
  replaceHorarios
} from '../../services/api.ts';
import type { WeeklyHorarioInput } from '../../services/api.ts';
import WeeklyScheduleEditor from '../../../../common/addressSchedule/WeeklyScheduleEditor.tsx';
import '../../styles/availability-settings.css';

interface Horario {
  faixa_id?: string;
  id?: string | number;
  dia_semana: number;
  turno: number;
  hora_inicio: string;
  hora_fim: string;
  aberto: boolean;
  [key: string]: any;
}

interface AvailabilitySettingsProps {
  onClose: () => void;
}

interface BaseHorario {
  faixa_id?: string;
  dia_semana: number;
  dia_label: string;
  hora_inicio: string;
  hora_fim: string;
  fechado: boolean;
}

interface ExtraHorario {
  faixa_id?: string;
  dia_semana: number;
  hora_inicio: string;
  hora_fim: string;
}

const MAX_INTERVALOS_POR_DIA = 10;

const diasSemana = [
  'Domingo',
  'Segunda',
  'Terça',
  'Quarta',
  'Quinta',
  'Sexta',
  'Sábado',
];

const defaultBaseHorarios = (): BaseHorario[] => (
  diasSemana.map((dia_label, dia_semana) => ({
    dia_semana,
    dia_label,
    hora_inicio: '11:00',
    hora_fim: '15:00',
    fechado: true,
  }))
);

const normalizeTime = (value: any) => {
  if (!value) return '';
  return String(value).slice(0, 5);
};

const parseTimeToMinutes = (value: string): number | null => {
  const normalized = String(value || '').trim();
  const match = normalized.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return (hours * 60) + minutes;
};

const buildStateFromApi = (rows: Horario[]) => {
  const grouped = new Map<number, Horario[]>();
  (rows || [])
    .filter((row) => row && row.aberto)
    .forEach((row) => {
      const dia = Number(row.dia_semana);
      if (!Number.isInteger(dia) || dia < 0 || dia > 6) return;
      if (!grouped.has(dia)) grouped.set(dia, []);
      grouped.get(dia)?.push(row);
    });

  const base = defaultBaseHorarios();
  const extras: ExtraHorario[] = [];

  for (let dia = 0; dia < 7; dia += 1) {
    const dayRows = (grouped.get(dia) || [])
      .sort((a, b) => {
        const aStart = parseTimeToMinutes(normalizeTime(a?.hora_inicio)) ?? 0;
        const bStart = parseTimeToMinutes(normalizeTime(b?.hora_inicio)) ?? 0;
        return aStart - bStart;
      });

    if (dayRows.length === 0) {
      base[dia] = {
        ...base[dia],
        fechado: true,
      };
      continue;
    }

    const [principal, ...rest] = dayRows;
    base[dia] = {
      ...base[dia],
      faixa_id: principal.faixa_id,
      hora_inicio: normalizeTime(principal.hora_inicio),
      hora_fim: normalizeTime(principal.hora_fim),
      fechado: false,
    };

    rest.forEach((item) => {
      extras.push({
        dia_semana: dia,
        faixa_id: item.faixa_id,
        hora_inicio: normalizeTime(item.hora_inicio),
        hora_fim: normalizeTime(item.hora_fim),
      });
    });
  }

  return { base, extras };
};

const AvailabilitySettings: React.FC<AvailabilitySettingsProps> = ({ onClose }) => {
  const [horariosBase, setHorariosBase] = useState<BaseHorario[]>(() => defaultBaseHorarios());
  const [horariosExtras, setHorariosExtras] = useState<ExtraHorario[]>([]);
  const [loadError, setLoadError] = useState(false);
  const savingRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadHorarios();
    // Carrega horários só no mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHorarios = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const data = await fetchHorarios();
      if (!Array.isArray(data)) throw new Error('Resposta de horários inválida.');
      const horarios = data;

      const { base, extras } = buildStateFromApi(horarios);
      setHorariosBase(base);
      setHorariosExtras(extras);
    } catch (error) {
      setLoadError(true);
      console.error('Erro ao carregar horários:', error);
      toast.error('Não foi possível carregar os horários do Reservo.');
    } finally {
      setLoading(false);
    }
  };

  const extrasPorDia = useMemo(() => {
    return horariosExtras.reduce((acc, item, index) => {
      const dia = Number(item?.dia_semana ?? 0);
      if (!acc[dia]) acc[dia] = [];
      acc[dia].push({ ...item, _index: index });
      return acc;
    }, {} as Record<number, Array<ExtraHorario & { _index: number }>>);
  }, [horariosExtras]);

  const getIntervalsForDay = (base: BaseHorario) => {
    if (base.fechado) return [];

    return [
      { faixa_id: base.faixa_id, hora_inicio: base.hora_inicio, hora_fim: base.hora_fim },
      ...(extrasPorDia[base.dia_semana] || []).map((item) => ({
        hora_inicio: item.hora_inicio,
        hora_fim: item.hora_fim,
      })),
    ];
  };

  const handleBaseChange = (index: number, field: 'hora_inicio' | 'hora_fim' | 'fechado', value: string | boolean) => {
    setHorariosBase((prev) => {
      const next = [...prev];
      const current = { ...next[index] };
      if (field === 'fechado') {
        const fechado = Boolean(value);
        current.fechado = fechado;
        if (!fechado && (!current.hora_inicio || !current.hora_fim)) {
          current.hora_inicio = current.hora_inicio || '11:00';
          current.hora_fim = current.hora_fim || '15:00';
        }
      } else {
        current[field] = String(value);
      }
      next[index] = current;
      return next;
    });
  };

  const handleExtraChange = (index: number, field: 'hora_inicio' | 'hora_fim', value: string) => {
    setHorariosExtras((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddExtra = (dia: number) => {
    if ((extrasPorDia[dia]?.length || 0) + 1 >= MAX_INTERVALOS_POR_DIA) return;
    setHorariosExtras((prev) => ([
      ...prev,
      { dia_semana: dia, hora_inicio: '', hora_fim: '' },
    ]));
  };

  const handleRemoveExtra = (index: number) => {
    setHorariosExtras((prev) => prev.filter((_, idx) => idx !== index));
  };

  const scheduleDays = horariosBase.map((base) => ({
    day: String(base.dia_semana),
    label: base.dia_label,
    closed: base.fechado,
    periods: getIntervalsForDay(base).map((interval) => ({
      start: interval.hora_inicio,
      end: interval.hora_fim,
    })),
  }));

  const updateSchedulePeriod = (
    dayIndex: number,
    periodIndex: number,
    field: 'start' | 'end',
    value: string,
  ) => {
    const apiField = field === 'start' ? 'hora_inicio' : 'hora_fim';
    if (periodIndex === 0) {
      handleBaseChange(dayIndex, apiField, value);
      return;
    }
    const extra = (extrasPorDia[dayIndex] || [])[periodIndex - 1];
    if (extra) handleExtraChange(extra._index, apiField, value);
  };

  const removeSchedulePeriod = (dayIndex: number, periodIndex: number) => {
    const extras = extrasPorDia[dayIndex] || [];
    if (periodIndex > 0) {
      const extra = extras[periodIndex - 1];
      if (extra) handleRemoveExtra(extra._index);
      return;
    }

    const [nextBase] = extras;
    if (!nextBase) {
      handleBaseChange(dayIndex, 'fechado', true);
      return;
    }

    setHorariosBase((prev) => prev.map((base, index) => index === dayIndex
      ? { ...base, faixa_id: nextBase.faixa_id, hora_inicio: nextBase.hora_inicio, hora_fim: nextBase.hora_fim }
      : base));
    handleRemoveExtra(nextBase._index);
  };

  const replicateScheduleToAll = (period: { start: string; end: string }) => {
    setHorariosBase((prev) => prev.map((base) => ({
      ...base,
      fechado: false,
      hora_inicio: period.start,
      hora_fim: period.end,
    })));
    setHorariosExtras([]);
  };

  const buildPayloadByTurno = () => {
    const payloadByTurno = new Map<number, WeeklyHorarioInput[]>();

    for (let dia = 0; dia < 7; dia += 1) {
      const base = horariosBase[dia];
      if (!base || base.fechado) {
        continue;
      }

      const dayIntervals = [
        { faixa_id: base.faixa_id, hora_inicio: base.hora_inicio, hora_fim: base.hora_fim },
        ...(extrasPorDia[dia] || []).map((item) => ({
          faixa_id: item.faixa_id,
          hora_inicio: item.hora_inicio,
          hora_fim: item.hora_fim,
        })),
      ];

      if (dayIntervals.length > MAX_INTERVALOS_POR_DIA) {
        return {
          error: `${diasSemana[dia]} possui mais de ${MAX_INTERVALOS_POR_DIA} intervalos.`,
          payloadByTurno,
        };
      }

      const segments: Array<{ faixa_id?: string; start: number; end: number; hora_inicio: string; hora_fim: string }> = [];
      for (const interval of dayIntervals) {
        const inicio = String(interval.hora_inicio || '').trim();
        const fim = String(interval.hora_fim || '').trim();
        const start = parseTimeToMinutes(inicio);
        const end = parseTimeToMinutes(fim);

        if (start === null || end === null) {
          return {
            error: `Preencha horários válidos para ${diasSemana[dia]}.`,
            payloadByTurno,
          };
        }

        if (start >= end) {
          return {
            error: `Em ${diasSemana[dia]}, o horário de início deve ser menor que o horário de fim.`,
            payloadByTurno,
          };
        }

        segments.push({ faixa_id: interval.faixa_id, start, end, hora_inicio: inicio, hora_fim: fim });
      }

      segments.sort((a, b) => a.start - b.start);
      for (let i = 1; i < segments.length; i += 1) {
        if (segments[i].start < segments[i - 1].end) {
          return {
            error: `Os horários de ${diasSemana[dia]} estão sobrepostos.`,
            payloadByTurno,
          };
        }
      }

      segments.forEach((segment, turnoIndex) => {
        if (!payloadByTurno.has(turnoIndex)) payloadByTurno.set(turnoIndex, []);
        payloadByTurno.get(turnoIndex)?.push({
          dia_semana: dia,
          ...(segment.faixa_id ? { faixa_id: segment.faixa_id } : {}),
          hora_inicio: segment.hora_inicio,
          hora_fim: segment.hora_fim,
          aberto: true,
        });
      });
    }

    return { error: null as string | null, payloadByTurno };
  };

  const handleSave = async () => {
    if (savingRef.current || loading || loadError) return;

    const { error, payloadByTurno } = buildPayloadByTurno();
    if (error) {
      toast.error(error);
      return;
    }

    try {
      savingRef.current = true;
      setSaving(true);
      await replaceHorarios(Array.from(payloadByTurno.values()).flat());

      toast.success('Horários salvos com sucesso.');
      onClose();
    } catch (saveError) {
      console.error('Erro ao salvar horários:', saveError);
      toast.error((saveError as any)?.response?.data?.error || (saveError as any)?.response?.data?.message || 'Erro ao salvar horários.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="availability-settings-overlay">
        <div className="availability-settings">
          <div className="availability-loading">
            <Loader size={22} className="availability-loading-icon" />
            <span>Carregando horários...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="availability-settings-overlay">
      <div className="availability-settings">
        <div className="settings-header">
          <h3>Configuração de Disponibilidade</h3>
          <button className="close-btn" onClick={onClose} disabled={saving} aria-label="Fechar">
            <X size={18} />
          </button>
        </div>

        <div className="settings-content">
          {loadError && (
            <div role="alert">
              <p>Não foi possível carregar os horários. Tente novamente antes de salvar.</p>
              <button type="button" onClick={loadHorarios}>Tentar novamente</button>
            </div>
          )}
          <WeeklyScheduleEditor
            days={scheduleDays}
            inputClassName="rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-800 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
            disabled={saving || loadError}
            allowOvernight={false}
            maxPeriodsPerDay={MAX_INTERVALOS_POR_DIA}
            onToggleDayClosed={(dayIndex, closed) => handleBaseChange(dayIndex, 'fechado', closed)}
            onAddPeriod={(dayIndex) => handleAddExtra(dayIndex)}
            onUpdatePeriod={updateSchedulePeriod}
            onRemovePeriod={removeSchedulePeriod}
            onReplicateAll={replicateScheduleToAll}
          />
        </div>

        <div className="settings-actions">
          <button className="btn-cancel" onClick={onClose} disabled={saving}>Cancelar</button>
          <button
            className="btn-save"
            onClick={handleSave}
            disabled={saving || loadError}
          >
            {saving ? 'Salvando...' : 'Salvar alterações'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvailabilitySettings;

